// app/components/AdvancedChart/plugins/EventAlerts/StockChart.tsx
'use client'

import React, { useRef, useEffect, useState } from 'react'
import {
  createChart,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  CandlestickData,
  CrosshairMode,
  CandlestickSeries,
  LineSeries, // may be needed for markers primitive
  createSeriesMarkers
} from 'lightweight-charts'

// Define props and types as before.
interface StockChartProps {
  ticker?: string
}

interface CandleData {
  time: string // e.g. "2025-01-01"
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export interface StockEvent {
  id: string
  type: string
  date: string // ISO string date for the event
  title: string
  description: string
}

interface TooltipData {
  event: StockEvent
  x: number
  y: number
}

const StockChart: React.FC<StockChartProps> = ({ ticker }) => {
  // Container reference for the chart.
  const chartContainerRef = useRef<HTMLDivElement>(null)
  // Chart and series refs.
  const chartRef = useRef<IChartApi | null>(null)
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  
  const [candles, setCandles] = useState<CandleData[]>([])
  const [events, setEvents] = useState<StockEvent[]>([])
  const [tooltip, setTooltip] = useState<TooltipData | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  // Keep the latest events in a ref for the crosshair callback.
  const eventsRef = useRef<StockEvent[]>([])
  useEffect(() => {
    eventsRef.current = events
  }, [events])

  // Setup an IntersectionObserver for lazy-loading.
  useEffect(() => {
    if (!chartContainerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.1 } // Trigger when at least 10% is visible.
    )

    observer.observe(chartContainerRef.current)
    return () => observer.disconnect()
  }, [])

  // --- FETCH CANDLESTICK DATA ---
  useEffect(() => {
    if (!isVisible) return // Only fetch when visible.
    const url = ticker ? `/api/chart?symbol=${ticker}` : '/api/chart'
    fetch(url)
      .then(res => {
        if (!res.ok) {
          console.error(`Error fetching history for ${ticker}:`, res.status, res.statusText)
          throw new Error('Network response not ok')
        }
        return res.json()
      })
      .then(data => {
        setCandles(data)
      })
      .catch(err => console.error('Fetch error:', err))
  }, [ticker, isVisible])

  // --- FETCH EVENTS DATA ---
  useEffect(() => {
    if (!isVisible) return // Only fetch when visible.
    const url = ticker ? `/api/events?symbol=${ticker}` : '/api/events'
    fetch(url)
      .then(res => {
        if (!res.ok) {
          console.error('Error fetching events:', res.status, res.statusText)
          throw new Error('Network response not ok')
        }
        return res.json()
      })
      .then(data => setEvents(data.events))
      .catch(err => console.error('Fetch error:', err))
  }, [ticker, isVisible])

  // --- INITIALIZE CHART AND SET CANDLESTICK DATA ---
  useEffect(() => {
    if (!chartContainerRef.current) return

    // Create the chart only once.
    if (!chartRef.current) {
      chartRef.current = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 300,
        layout: {
          background: { color: '#fff' },
          textColor: '#000',
          attributionLogo: false
        },
        crosshair: {
          mode: CrosshairMode.Normal
        },
        handleScroll: {
          mouseWheel: true,
          pressedMouseMove: true
        },
        handleScale: {
          axisPressedMouseMove: true,
          mouseWheel: true,
          pinch: true
        }
      })
      // Create a candlestick series using the new API.
      candleSeriesRef.current = chartRef.current.addSeries(CandlestickSeries, {})
    }

    // Format candle data for lightweight‑charts.
    const formattedData = candles.map(candle => {
      const time = Math.floor(new Date(candle.time).getTime() / 1000) as UTCTimestamp
      return {
        time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close
      } as CandlestickData
    })
    if (candleSeriesRef.current) {
      candleSeriesRef.current.setData(formattedData)
    }
  }, [candles])

  // --- ADD EVENT MARKERS TO THE CHART USING NEW MARKERS PRIMITIVE ---
  useEffect(() => {
    if (candleSeriesRef.current && events.length > 0) {
      const markers = events.map(event => {
        // Convert event date to local midnight.
        const eventDate = new Date(event.date)
        const localMidnight = new Date(
          eventDate.getFullYear(),
          eventDate.getMonth(),
          eventDate.getDate()
        )
        return {
          time: Math.floor(localMidnight.getTime() / 1000) as UTCTimestamp,
          position: 'aboveBar' as const,
          color: '#88a1ac',
          shape: 'circle' as const,
          text: event.title,
        }
      })
      // In v5, use createSeriesMarkers to manage markers.
      createSeriesMarkers(candleSeriesRef.current, markers)
    }
  }, [events])

  // --- HANDLE MARKER HOVER ---
  useEffect(() => {
    if (!chartRef.current || !chartContainerRef.current) return

    const container = chartContainerRef.current
    const handleMouseMove = (e: MouseEvent) => {
      if (!chartRef.current) return
      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      // Convert the x-coordinate back to time.
      const coordinate = chartRef.current.timeScale().coordinateToTime(x) as number
      if (coordinate) {
        // Find an event near the mouse position (within 24 hours)
        const nearestEvent = events.find(event => {
          const eventTime = Math.floor(new Date(event.date).getTime() / 1000)
          return Math.abs(eventTime - coordinate) < 86400 // 24 hours threshold
        })
        if (nearestEvent) {
          setTooltip({
            event: nearestEvent,
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          })
        } else {
          setTooltip(null)
        }
      }
    }
    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseleave', () => setTooltip(null))
    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseleave', () => setTooltip(null))
    }
  }, [events])

  return (
    <div style={{ position: 'relative' }}>
      <div ref={chartContainerRef} style={{ width: '100%', height: '300px' }} />
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            left: `${tooltip.x}px`,
            top: `${tooltip.y - 60}px`, // Position above the cursor
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            maxWidth: '200px',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'all 0.1s ease-out'
          }}
        >
          <strong>{tooltip.event.title}</strong>
          <br />
          {tooltip.event.description}
          <br />
          <small>{new Date(tooltip.event.date).toLocaleDateString()}</small>
        </div>
      )}
    </div>
  )
}

export default StockChart
